import { deploymentSetting } from '../../runtime-config.js';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMcpInfo, type McpInvocation, type McpTool } from '../../api/mcp-info.js';
import { useApiKeys } from '../../api/api-keys.js';

/**
 * Admin → Claude Code
 *
 * One-stop setup page for the MCP integration. Shows what Claude Code
 * will get (tool catalog), where the server is (API URL), how to
 * configure it (JSON snippet with the user's live API origin), and a
 * link to the API keys page for minting.
 */
export function ClaudeCodePage(): JSX.Element {
  const { data: info, isLoading: infoLoading, error: infoError } = useMcpInfo();
  const { data: keys } = useApiKeys();
  const [selectedInvocation, setSelectedInvocation] = useState(0);
  const activeKeys = useMemo(
    () =>
      (keys ?? []).filter(
        (k) => !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt).getTime() > Date.now()),
      ),
    [keys],
  );

  // The SPA serves at sage.gocanopy.io but the API is at
  // api.sage.gocanopy.io — using window.location.origin would point
  // the MCP client at the SPA host, which 404s every API call. Read
  // the same VITE_API_BASE_URL the SPA's own api client uses, fall
  // back to deriving 'api.<sage host>' from window.location, fall
  // back to a placeholder when neither is available (SSR / dev).
  const apiUrlValue = inferApiBase();

  // CSAGE-85: prefer the HTTP transport URL the API advertises;
  // fall back to deriving from the API base if the API hasn't been
  // upgraded yet (rolling-deploy gap).
  const httpMcpUrl =
    info?.transport?.http.url && !info.transport.http.url.startsWith('<')
      ? info.transport.http.url
      : `${apiUrlValue}/reporting/api/mcp`;

  const invocation: McpInvocation | null = info?.invocations[selectedInvocation] ?? null;

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Claude Code integration</h1>
          <p className="page-title-subtitle">
            Connect Claude Code (or any MCP-compatible client) to this tenant so agents can author
            templates, build widget dashboards, and publish on your behalf — using the same schemas
            and audit trail as the web app.
          </p>
        </div>
        {info && (
          <div className="page-title-actions">
            <span className="badge badge--accent">{info.tools.length} tools</span>
            <span className="text-dim text-xs">v{info.serverVersion}</span>
          </div>
        )}
      </header>

      {infoLoading && <div className="text-dim">Loading…</div>}
      {infoError && <div className="alert alert--danger">Failed to load MCP info.</div>}
      {info && (
        <div className="stack-lg">
          {/* Recommended path: HTTP connector. Two fields, no install.
             CSAGE-85. */}
          <section
            className="cc-step"
            style={{
              border: '1px solid var(--color-accent-border)',
              borderLeftWidth: 4,
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
              background: 'var(--color-accent-softer)',
            }}
          >
            <div className="cc-step-num">★</div>
            <div className="cc-step-body">
              <h2>
                Recommended — add as a connector{' '}
                <span className="badge badge--accent" style={{ marginLeft: 8 }}>
                  No install
                </span>
              </h2>
              <p>
                Two fields. No clone, no build, no path resolution. Works in Claude Desktop's
                Connectors UI and in Claude Code via <code>~/.claude.json</code>.
              </p>

              <div className="cc-quickconn-grid">
                <div className="cc-quickconn-field">
                  <label className="text-dim text-xs">URL</label>
                  <CopyableValue value={httpMcpUrl} />
                </div>
                <div className="cc-quickconn-field">
                  <label className="text-dim text-xs">Auth</label>
                  <code className="cc-quickconn-static">Authorization: Bearer cnr_…</code>
                </div>
              </div>

              <div className="cc-quickconn-howto">
                <h3 style={{ fontSize: 'var(--font-size-md)', margin: '12px 0 6px' }}>
                  Claude Desktop
                </h3>
                <ol className="text-sm">
                  <li>Settings → Connectors → Add Custom Connector</li>
                  <li>
                    Name: <code>Canopy Sage</code>
                  </li>
                  <li>URL: paste the URL above</li>
                  <li>
                    Auth → Bearer token → paste your <code>cnr_…</code> from{' '}
                    <Link to="/admin/api-keys">Admin → API Keys</Link>
                  </li>
                  <li>
                    Restart Claude Desktop. Ask:{' '}
                    <em>"List the frameworks available in Canopy Sage."</em>
                  </li>
                </ol>

                <h3 style={{ fontSize: 'var(--font-size-md)', margin: '12px 0 6px' }}>
                  Claude Code
                </h3>
                <p className="text-sm">
                  Add to <code>~/.claude.json</code> or your project's <code>.mcp.json</code>:
                </p>
                <HttpConfigSnippet url={httpMcpUrl} />
              </div>
            </div>
          </section>

          <details className="cc-advanced">
            <summary className="text-dim text-sm" style={{ cursor: 'pointer' }}>
              Advanced — install locally as a stdio MCP server
            </summary>
            <div style={{ marginTop: 16 }}>
              {/* Step 1: Key */}
              <section className="cc-step">
                <div className="cc-step-num">1</div>
                <div className="cc-step-body">
                  <h2>Mint an API key</h2>
                  <p>
                    Keys are scoped to you. Give the key a label you'll recognize (e.g.{' '}
                    <code>my-laptop-claude</code>). Revoking any of your platform roles instantly
                    narrows every key you've minted.
                  </p>
                  <div className="row" style={{ gap: 12 }}>
                    <Link to="/admin/api-keys" className="btn btn--primary btn--sm">
                      Open API Keys
                    </Link>
                    <span className="text-dim text-sm">
                      {activeKeys.length} active key{activeKeys.length === 1 ? '' : 's'} on this
                      tenant.
                    </span>
                  </div>
                </div>
              </section>

              {/* Step 2: Install */}
              <section className="cc-step">
                <div className="cc-step-num">2</div>
                <div className="cc-step-body">
                  <h2>Build the MCP server on your machine</h2>
                  <p>
                    The Sage MCP server is a small Node binary that ships in the canopy-sage repo.
                    Clone the repo if you don't already have it, then build:
                  </p>
                  <code className="cc-invocation-cmd">
                    git clone git@ssh.bitbucket.org:banyanhills/canopy-sage.git
                    <br />
                    cd canopy-sage
                    <br />
                    pnpm install
                    <br />
                    pnpm --filter @canopy/mcp build
                  </code>
                  <p className="text-dim text-sm" style={{ marginTop: 8 }}>
                    Result: <code>apps/mcp/dist/index.js</code> in your repo checkout. The path to
                    that file is what you'll point Claude Code at in step 3 — make sure to use the
                    <strong> absolute </strong> path, not a relative one.
                  </p>
                  <details style={{ marginTop: 12 }}>
                    <summary className="text-dim text-sm" style={{ cursor: 'pointer' }}>
                      Other invocation styles (advanced)
                    </summary>
                    <div className="cc-invocation-tabs" style={{ marginTop: 8 }}>
                      {info.invocations.map((inv, i) => (
                        <button
                          key={inv.label}
                          type="button"
                          className={
                            'cc-invocation-tab' +
                            (i === selectedInvocation ? ' cc-invocation-tab--on' : '')
                          }
                          onClick={() => setSelectedInvocation(i)}
                        >
                          {inv.label}
                        </button>
                      ))}
                    </div>
                    {invocation && (
                      <div className="cc-invocation">
                        <div className="cc-invocation-hint">{invocation.hint}</div>
                        <code className="cc-invocation-cmd">
                          {invocation.command} {invocation.args.join(' ')}
                        </code>
                      </div>
                    )}
                  </details>
                </div>
              </section>

              {/* Step 3: Config */}
              <section className="cc-step">
                <div className="cc-step-num">3</div>
                <div className="cc-step-body">
                  <h2>Add to your MCP config</h2>
                  <p>Pick where Claude Code should see this MCP server:</p>
                  <ul className="text-sm">
                    <li>
                      <code>~/.claude.json</code> — Claude Code sees the server in{' '}
                      <strong>every</strong> project, every session.
                    </li>
                    <li>
                      <code>&lt;repo&gt;/.mcp.json</code> — only available when Claude Code opens
                      that repo. Cleaner if the API key is scoped to one tenant.
                    </li>
                  </ul>
                  <p>
                    Paste the JSON below into your chosen file. Replace the
                    <strong> two placeholders </strong>: the absolute path to
                    <code> apps/mcp/dist/index.js </code> in your repo checkout (from step 2), and
                    the <code>cnr_...</code> key plaintext you copied at step 1.
                  </p>
                  <ConfigSnippet apiUrl={apiUrlValue} invocation={invocation} />
                  <p className="text-dim text-sm">
                    Required environment:{' '}
                    {info.requiredEnv
                      .filter((e) => e.required)
                      .map((e) => e.name)
                      .join(', ')}
                    . Optional:{' '}
                    {info.requiredEnv
                      .filter((e) => !e.required)
                      .map((e) => e.name)
                      .join(', ') || '—'}
                    .
                  </p>
                  <div className="alert alert--info" style={{ marginTop: 12 }}>
                    <strong>Common stumble:</strong> the <code>CANOPY_REPORTING_API_URL</code> must
                    be the <strong>API</strong> origin, not the SPA origin. On this deployment the
                    API is at <code>{apiUrlValue}</code> — note the <code>api.</code> subdomain. The
                    SPA you're reading right now lives at a different host. Pasting the SPA URL here
                    is the #1 cause of "tools appear but every call 401s."
                  </div>
                </div>
              </section>

              {/* Step 4: Verify */}
              <section className="cc-step">
                <div className="cc-step-num">4</div>
                <div className="cc-step-body">
                  <h2>Verify</h2>
                  <p>
                    Restart Claude Code, then in a new session ask it:{' '}
                    <em>"List the frameworks available in Canopy Sage."</em> It should call{' '}
                    <code>list_frameworks</code> and return your tenant's catalog. The "Last used"
                    column on the API Keys page will also update.
                  </p>
                </div>
              </section>
            </div>
          </details>

          {/* Catalog */}
          <section className="cc-section">
            <header>
              <h2>Tool catalog</h2>
              <p className="text-dim text-sm">
                What this MCP deployment exposes. Every tool maps to a permission-checked REST
                endpoint; the caller's platform roles determine what they can actually do.
              </p>
            </header>
            <ToolCatalog tools={info.tools} />
          </section>
        </div>
      )}

      <ClaudeCodeStyles />
    </>
  );
}

function inferApiBase(): string {
  // 1. The configured API origin wins (runtime-config.ts — the container's
  //    SAGE_API_BASE_URL, or VITE_API_BASE_URL under vite dev).
  const fromEnv = (deploymentSetting('apiBaseUrl') ?? '').replace(/\/+$/u, '');
  if (fromEnv) return fromEnv;
  // 2. Heuristic: if the SPA is served at sage.<host>, the API is at
  //    api.sage.<host>. Holds for our dedicated-subdomain prod deploy.
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname.startsWith('sage.')) {
      return `${protocol}//api.${hostname}`;
    }
    // 3. Same-origin deploys (dev, shared-domain). API mounts under
    //    /reporting/api on the SPA's origin.
    return window.location.origin;
  }
  // 4. SSR / build / no window — placeholder.
  return 'https://api.sage.<your-tenant>.canopy.app';
}

function CopyableValue({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="cc-quickconn-copyable">
      <code>{value}</code>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? '✓ copied' : 'copy'}
      </button>
    </div>
  );
}

function HttpConfigSnippet({ url }: { url: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const snippet = useMemo(
    () =>
      JSON.stringify(
        {
          mcpServers: {
            'canopy-sage': {
              type: 'http',
              url,
              headers: {
                Authorization: 'Bearer cnr_…paste-your-key',
              },
            },
          },
        },
        null,
        2,
      ),
    [url],
  );
  return (
    <div className="cc-snippet">
      <pre>{snippet}</pre>
      <div className="cc-snippet-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={async () => {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? '✓ copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function ConfigSnippet({
  apiUrl,
  invocation,
}: {
  apiUrl: string;
  invocation: McpInvocation | null;
}): JSX.Element {
  const snippet = useMemo(() => {
    const args = invocation?.args ?? [];
    const command = invocation?.command ?? 'canopy-reporting-mcp';
    return JSON.stringify(
      {
        mcpServers: {
          'canopy-reporting': {
            command,
            ...(args.length > 0 ? { args } : {}),
            env: {
              CANOPY_REPORTING_API_URL: apiUrl,
              CANOPY_REPORTING_API_KEY: 'cnr_...',
            },
          },
        },
      },
      null,
      2,
    );
  }, [apiUrl, invocation]);

  return (
    <div className="cc-snippet">
      <pre>{snippet}</pre>
      <div className="cc-snippet-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            void navigator.clipboard.writeText(snippet);
          }}
        >
          Copy
        </button>
      </div>
    </div>
  );
}

function ToolCatalog({ tools }: { tools: McpTool[] }): JSX.Element {
  const byCategory: Record<string, McpTool[]> = {
    discovery: [],
    iteration: [],
    authoring: [],
    operations: [],
  };
  for (const t of tools) {
    if (!byCategory[t.category]) byCategory[t.category] = [];
    byCategory[t.category]!.push(t);
  }
  return (
    <div className="cc-catalog">
      <CatalogColumn
        title="Discovery"
        desc="Read-only introspection; safe to call liberally."
        tools={byCategory['discovery'] ?? []}
      />
      <CatalogColumn
        title="Iteration"
        desc="Validate + preview without persisting — the agent refinement loop."
        tools={byCategory['iteration'] ?? []}
      />
      <CatalogColumn
        title="Authoring"
        desc="Writes that land in audit + versioned history."
        tools={byCategory['authoring'] ?? []}
      />
      <CatalogColumn
        title="Operations"
        desc="Schedule + delivery lifecycle for live reports."
        tools={byCategory['operations'] ?? []}
      />
    </div>
  );
}

function CatalogColumn({
  title,
  desc,
  tools,
}: {
  title: string;
  desc: string;
  tools: McpTool[];
}): JSX.Element {
  return (
    <div className="cc-catalog-col">
      <h3>{title}</h3>
      <p className="text-dim text-sm">{desc}</p>
      <ul className="cc-tool-list">
        {tools.map((t) => (
          <li key={t.name}>
            <code>{t.name}</code>
            <p>{t.description}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ClaudeCodeStyles(): JSX.Element {
  return (
    <style>{`
      .cc-step {
        display: grid;
        grid-template-columns: 36px 1fr;
        gap: var(--space-4);
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-4);
      }
      .cc-step-num {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--color-accent);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 16px;
      }
      .cc-step-body h2 {
        margin: 0 0 6px;
        font-size: var(--font-size-lg);
      }
      .cc-step-body p {
        margin: 0 0 var(--space-3);
        color: var(--color-text-secondary);
      }
      .cc-invocation-tabs {
        display: flex;
        gap: 0;
        border-bottom: 1px solid var(--color-border-subtle);
        margin: var(--space-3) 0 var(--space-2);
      }
      .cc-invocation-tab {
        background: transparent;
        border: 0;
        border-bottom: 2px solid transparent;
        color: var(--color-text-secondary);
        padding: 8px 14px;
        cursor: pointer;
        font-size: var(--font-size-sm);
        font-weight: 600;
      }
      .cc-invocation-tab--on {
        color: var(--color-accent);
        border-bottom-color: var(--color-accent);
      }
      .cc-invocation {
        background: var(--color-surface-2);
        border-radius: var(--radius-sm);
        padding: var(--space-3);
      }
      .cc-invocation-hint {
        font-size: var(--font-size-xs);
        color: var(--color-text-dim);
        margin-bottom: 6px;
      }
      .cc-invocation-cmd {
        display: block;
        font-family: var(--font-mono);
        font-size: 13px;
        color: var(--color-text);
      }
      .cc-snippet {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        padding: var(--space-3);
        position: relative;
      }
      .cc-quickconn-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-3);
        margin-top: var(--space-3);
      }
      @media (max-width: 720px) {
        .cc-quickconn-grid { grid-template-columns: 1fr; }
      }
      .cc-quickconn-field label {
        display: block;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 4px;
      }
      .cc-quickconn-copyable {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        padding: var(--space-2) var(--space-3);
      }
      .cc-quickconn-copyable code {
        flex: 1;
        font-family: var(--font-mono);
        font-size: 13px;
        overflow-x: auto;
        white-space: nowrap;
      }
      .cc-quickconn-static {
        display: block;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        padding: var(--space-2) var(--space-3);
        font-family: var(--font-mono);
        font-size: 13px;
      }
      .cc-quickconn-howto {
        margin-top: var(--space-4);
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }
      .cc-advanced summary {
        padding: var(--space-2) 0;
      }
      .cc-snippet pre {
        margin: 0;
        font-size: 12px;
        font-family: var(--font-mono);
        overflow-x: auto;
        color: var(--color-text);
      }
      .cc-snippet-actions {
        position: absolute;
        top: 8px;
        right: 8px;
      }
      .cc-section {
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-4);
      }
      .cc-section header {
        margin-bottom: var(--space-3);
      }
      .cc-section h2 {
        margin: 0 0 4px;
      }
      .cc-catalog {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: var(--space-3);
      }
      .cc-catalog-col {
        background: var(--color-surface-2);
        border-radius: var(--radius-sm);
        padding: var(--space-3);
      }
      .cc-catalog-col h3 {
        margin: 0 0 4px;
        font-size: var(--font-size-sm);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-accent);
      }
      .cc-tool-list {
        list-style: none;
        padding: 0;
        margin: var(--space-2) 0 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .cc-tool-list li code {
        font-family: var(--font-mono);
        font-size: 12px;
        font-weight: 600;
        color: var(--color-text);
      }
      .cc-tool-list li p {
        margin: 2px 0 0;
        font-size: var(--font-size-xs);
        color: var(--color-text-secondary);
        line-height: 1.4;
      }
    `}</style>
  );
}
